class CosmicBackgroundController {
	constructor(canvas) {
		this.canvas = canvas;
		this.ctx = canvas.getContext('2d', { alpha: false });
		this.width = 0;
		this.height = 0;
		this.particles = [];
		this.frameId = 0;
		this.resizeObserver = null;
		this.config = {
			particleCount: 1500,
			pixelSize: 2,
			rotationSpeed: 0.0002,
			expansion: 1.5,
			arms: 3,
			armWidth: 0.4,
		};
		this.animate = this.animate.bind(this);
		this.handleResize = this.handleResize.bind(this);
	}

	attach(canvas) {
		if (this.canvas === canvas) return;

		this.canvas = canvas;
		this.ctx = canvas.getContext('2d', { alpha: false });
		this.observeCanvas();
		this.handleResize();
	}

	createParticle() {
		const { width, height, config } = this;
		const r = Math.random();
		const distance = r ** config.expansion * (Math.max(width, height) * 0.8);
		const armAngle =
			(Math.floor(Math.random() * config.arms) / config.arms) * Math.PI * 2;
		const twist = distance * 0.002;
		const dispersion = (Math.random() - 0.5) * config.armWidth * distance;
		const particleDistance = distance + dispersion;
		let baseAlpha =
			(1 - particleDistance / (Math.max(width, height) * 0.6)) * 0.8;

		if (baseAlpha < 0) {
			baseAlpha = 0.1;
		}

		return {
			baseAngle: armAngle + twist,
			distance: particleDistance,
			size:
				Math.floor(Math.random() * 2) === 0
					? config.pixelSize
					: config.pixelSize * 2,
			baseAlpha,
			blinkRate: Math.random() * 0.02,
			blinkPhase: Math.random() * Math.PI * 2,
		};
	}

	initParticles() {
		this.particles = [];

		for (let i = 0; i < this.config.particleCount; i += 1) {
			this.particles.push(this.createParticle());
		}
	}

	handleResize() {
		if (!(this.canvas instanceof HTMLCanvasElement) || !this.ctx) return;

		const rect = this.canvas.getBoundingClientRect();
		const nextWidth = Math.max(1, Math.round(rect.width));
		const nextHeight = Math.max(1, Math.round(rect.height));

		if (nextWidth === this.width && nextHeight === this.height) return;

		this.width = nextWidth;
		this.height = nextHeight;
		this.canvas.width = nextWidth;
		this.canvas.height = nextHeight;
		this.initParticles();
	}

	observeCanvas() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}

		if ('ResizeObserver' in window) {
			this.resizeObserver = new ResizeObserver(() => this.handleResize());
			this.resizeObserver.observe(this.canvas);
		}

		window.removeEventListener('resize', this.handleResize);
		window.addEventListener('resize', this.handleResize, { passive: true });
	}

	drawParticle(particle, time) {
		const { ctx, width, height, config } = this;
		const speedMod = 1 / (1 + particle.distance * 0.001);
		const currentAngle =
			particle.baseAngle + time * config.rotationSpeed * speedMod;
		const x = width / 2 + Math.cos(currentAngle) * particle.distance;
		const y = height / 2 + Math.sin(currentAngle) * particle.distance;

		if (x <= 0 || x >= width || y <= 0 || y >= height) {
			return;
		}

		const currentAlpha =
			particle.baseAlpha *
			(0.7 + 0.3 * Math.sin(time * particle.blinkRate + particle.blinkPhase));
		const pixelX = Math.floor(x / particle.size) * particle.size;
		const pixelY = Math.floor(y / particle.size) * particle.size;

		ctx.fillStyle = `rgba(255, 255, 255, ${currentAlpha})`;
		ctx.fillRect(pixelX, pixelY, particle.size, particle.size);
	}

	animate(time) {
		if (!this.ctx) return;

		this.ctx.fillStyle = '#000000';
		this.ctx.fillRect(0, 0, this.width, this.height);

		for (const particle of this.particles) {
			this.drawParticle(particle, time);
		}

		this.frameId = window.requestAnimationFrame(this.animate);
	}

	start() {
		if (this.frameId) return;

		this.observeCanvas();
		this.handleResize();
		this.frameId = window.requestAnimationFrame(this.animate);
	}
}

export default function cosmicBackground() {
	const canvas = document.getElementById('cosmic-canvas');

	if (!(canvas instanceof HTMLCanvasElement)) {
		return;
	}

	const cosmicState = window.__cosmicBackgroundState;

	if (cosmicState?.controller) {
		cosmicState.controller.attach(canvas);
		cosmicState.controller.start();
		return;
	}

	const controller = new CosmicBackgroundController(canvas);
	window.__cosmicBackgroundState = { controller };
	controller.start();
}
